import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/shared/services/api.service';
import { LrsService, LrsRoute } from 'src/app/shared/services/lrs.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

@Component({
  selector: 'app-add-inspection-stage',
  templateUrl: './add-inspection-stage.component.html',
  styleUrl: './add-inspection-stage.component.css',
})
export class AddInspectionStageComponent implements OnInit {
  inspectionForm!: FormGroup;
  selectedDomain = 'Road';
  domainOptions = [
    'Residential', 'Commercial', 'Educational', 'Water', 'Power', 'Gas', 'Telecom', 'Road', 'Consultancy',
  ];
  recordId: number | null = null;
  saving = false;
  lrsRoutes: LrsRoute[] = [];

  domainFields: { [key: string]: { key: string; label: string; type: string; value?: unknown }[] } = {
    Residential: [
      { key: 'fireSafetyCompliance', label: 'Fire Safety Compliance', type: 'text', value: '' },
      { key: 'ventilationCheck', label: 'Ventilation Check', type: 'text', value: '' },
    ],
    Commercial: [
      { key: 'elevatorFunctionality', label: 'Elevator Functionality', type: 'text', value: '' },
      { key: 'facadeIntegrity', label: 'Façade Integrity', type: 'text', value: '' },
    ],
    Educational: [
      { key: 'labSafetyInspection', label: 'Lab Safety Inspection', type: 'text', value: '' },
      { key: 'accessibilityAudit', label: 'Accessibility Audit', type: 'text', value: '' },
    ],
    Water: [
      { key: 'waterQualityCheck', label: 'Water Quality Check', type: 'text', value: '' },
      { key: 'leakDetection', label: 'Leak Detection', type: 'text', value: '' },
    ],
    Power: [
      { key: 'voltageStabilityTest', label: 'Voltage Stability Test', type: 'text', value: '' },
      { key: 'transformerLoadTest', label: 'Transformer Load Test', type: 'text', value: '' },
    ],
    Gas: [
      { key: 'pressureTestResult', label: 'Pressure Test Result', type: 'text', value: '' },
      { key: 'leakDetectionStatus', label: 'Leak Detection Status', type: 'text', value: '' },
    ],
    Telecom: [
      { key: 'signalStrengthTest', label: 'Signal Strength Test', type: 'text', value: '' },
      { key: 'equipmentHealthCheck', label: 'Equipment Health Check', type: 'text', value: '' },
    ],
    Road: [
      { key: 'pavementThickness', label: 'Pavement Thickness', type: 'text', value: '' },
      { key: 'surfaceConditionIndex', label: 'Surface Condition Index', type: 'number', value: '' },
    ],
    Consultancy: [
      { key: 'complianceAuditNotes', label: 'Compliance Audit Notes', type: 'text', value: '' },
      { key: 'clientReviewSummary', label: 'Client Review Summary', type: 'text', value: '' },
    ],
  };

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private lrsService: LrsService,
    private toastrMsgService: ToastrMsgService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.inspectionForm = this.fb.group({
      inspection_id: ['', Validators.required],
      project_name: ['', Validators.required],
      project_id: [''],
      inspection_date: ['', Validators.required],
      inspector_name: ['', Validators.required],
      inspection_type: ['', Validators.required],
      findings_summary: [''],
      compliance_status: [''],
      corrective_action_plan: [''],
      next_inspection_date: [''],
      inspection_report_ref: [''],
      domain: ['Road', Validators.required],
      lrsRouteId: [null as number | null],
      lrsFromKm: [null as number | null],
      lrsToKm: [null as number | null],
      lrsChainage: [''],
    });
    this.onDomainChange(this.selectedDomain);
    this.loadLrsRoutes();

    this.route.params.subscribe((params) => {
      const id = params['id'];
      if (id) {
        this.recordId = Number(id);
        this.loadRecord(this.recordId);
      }
    });
  }

  private loadRecord(id: number): void {
    this.apiService.getProjectInspectionStageById(id).subscribe({
      next: (res: { data?: Record<string, unknown> }) => {
        const row = res.data || (res as Record<string, unknown>);
        const domainData = (row['domain_data'] as Record<string, unknown>) || {};
        this.inspectionForm.patchValue({
          inspection_id: row['inspection_ref'],
          project_name: row['project_name'],
          project_id: row['project_id'],
          inspection_date: row['inspection_date'],
          inspector_name: row['inspector_name'],
          inspection_type: row['inspection_type'],
          findings_summary: row['findings_summary'],
          compliance_status: row['compliance_status'],
          corrective_action_plan: row['corrective_action_plan'],
          next_inspection_date: row['next_inspection_date'],
          inspection_report_ref: row['inspection_report_ref'],
          domain: row['domain'] || 'Road',
        });
        this.onDomainChange(String(row['domain'] || 'Road'));
        this.inspectionForm.patchValue(domainData);
        const lrs = (domainData['lrs'] as Record<string, unknown>) || {};
        this.inspectionForm.patchValue({
          lrsRouteId: lrs['route_id'] != null ? Number(lrs['route_id']) : null,
          lrsFromKm: lrs['from_km'] != null ? Number(lrs['from_km']) : null,
          lrsToKm: lrs['to_km'] != null ? Number(lrs['to_km']) : null,
          lrsChainage: lrs['chainage'] || '',
        });
        this.loadLrsRoutes(row['project_id'] != null ? String(row['project_id']) : undefined);
      },
      error: (err) => this.toastrMsgService.error(err?.error?.message || 'Failed to load inspection'),
    });
  }

  onDomainChange(domain: string): void {
    this.selectedDomain = domain;
    Object.keys(this.inspectionForm.controls).forEach((key) => {
      if (
        key !== 'domain' &&
        this.domainFields[domain]?.find((field) => field.key === key) === undefined &&
        Object.values(this.domainFields).flat().some((field) => field.key === key)
      ) {
        this.inspectionForm.removeControl(key);
      }
    });
    (this.domainFields[domain] || []).forEach((field) => {
      if (!this.inspectionForm.contains(field.key)) {
        this.inspectionForm.addControl(field.key, this.fb.control(field.value ?? ''));
      }
    });
  }

  private buildPayload(): Record<string, unknown> {
    const v = this.inspectionForm.value;
    const domainKeys = (this.domainFields[this.selectedDomain] || []).map((f) => f.key);
    const domain_data: Record<string, unknown> = {};
    domainKeys.forEach((k) => {
      if (v[k] !== undefined && v[k] !== '') domain_data[k] = v[k];
    });
    if (v.lrsRouteId) {
      domain_data['lrs'] = {
        route_id: v.lrsRouteId,
        from_km: v.lrsFromKm,
        to_km: v.lrsToKm,
        chainage: v.lrsChainage || undefined,
      };
    }
    return {
      inspection_ref: v.inspection_id,
      project_name: v.project_name,
      project_id: v.project_id || null,
      inspection_date: v.inspection_date,
      inspector_name: v.inspector_name,
      inspection_type: v.inspection_type,
      findings_summary: v.findings_summary,
      compliance_status: v.compliance_status,
      corrective_action_plan: v.corrective_action_plan,
      next_inspection_date: v.next_inspection_date || null,
      inspection_report_ref: v.inspection_report_ref,
      domain: v.domain,
      domain_data,
      stage: 'Inspection',
      status: v.compliance_status === 'Approved' ? 'completed' : 'in_progress',
    };
  }

  loadLrsRoutes(projectId?: string): void {
    this.lrsService.listRoutes(projectId ? { project_id: projectId } : undefined).subscribe({
      next: (res) => {
        this.lrsRoutes = res.data || [];
      },
      error: () => {
        this.lrsRoutes = [];
      },
    });
  }

  openInspectionOnMap(): void {
    const v = this.inspectionForm.value;
    const queryParams: Record<string, string | number> = { tab: 'lrs' };
    if (v.lrsRouteId) queryParams['route_id'] = v.lrsRouteId;
    if (v.lrsFromKm != null) queryParams['from_km'] = v.lrsFromKm;
    if (v.lrsToKm != null) queryParams['to_km'] = v.lrsToKm;
    if (v.project_id) queryParams['project_id'] = v.project_id;
    void this.router.navigate(['/home/geospatial'], { queryParams });
  }

  onSubmit(): void {
    if (!this.inspectionForm.valid) {
      this.inspectionForm.markAllAsTouched();
      return;
    }
    this.saving = true;
    const payload = this.buildPayload();
    const req = this.recordId
      ? this.apiService.updateProjectInspectionStage(this.recordId, payload)
      : this.apiService.saveProjectInspectionStage(payload);

    req.subscribe({
      next: () => {
        this.saving = false;
        this.toastrMsgService.success(this.recordId ? 'Inspection updated' : 'Inspection saved');
        this.router.navigate(['/home/inspection-stage-list']);
      },
      error: (err) => {
        this.saving = false;
        this.toastrMsgService.error(err?.error?.message || 'Save failed');
      },
    });
  }
}
